import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { PATH_HOME } from "../constants/Paths";
import { useAlert } from "../components/providers/AlertProvider";

/**
 * Auth Guard Hook - Protects routes that require authentication
 * Redirects to agent page if user is not authenticated
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.requireSession - Whether session token is required
 * @param {boolean} options.requireProfile - Whether user profile is required  
 * @param {boolean} options.requireAgent - Whether agent is required
 * @param {string} options.redirectPath - Custom redirect path (defaults to PATH_HOME)
 * @param {string} options.alertTitle - Custom alert title
 * @param {string} options.alertMessage - Custom alert message
 */
export const useAuthGuard = (options = {}) => {
  const {
    requireSession = true,
    requireProfile = true,
    requireAgent = true,
    redirectPath = PATH_HOME,
    alertTitle = "Authentication Required",
    alertMessage = "Please login again to continue."
  } = options;

  const navigate = useNavigate();
  const { triggerAlert } = useAlert();

  const sessionToken = useSelector((state) => state.sessionToken);
  const userProfile = useSelector((state) => state.userProfile);
  const agent = useSelector((state) => state.agent);

  useEffect(() => {
    let shouldRedirect = false;
    let reason = "";

    // Check session token
    if (requireSession && !sessionToken) {
      shouldRedirect = true;
      reason = "No session token";
    }

    // Check user profile
    if (requireProfile && !userProfile) {
      shouldRedirect = true;
      reason = "No user profile";
    }

    // Check agent
    if (requireAgent && !agent) {
      shouldRedirect = true;
      reason = "No agent selected";
    }

    if (shouldRedirect) {
      console.warn(`[AuthGuard] Redirecting to ${redirectPath}: ${reason}`);
      
      // Show alert to user
      triggerAlert(alertTitle, alertMessage);
      
      // Clear any problematic state
      try {
        // Don't clear everything, just navigate
        navigate(redirectPath, { replace: true });
      } catch (navError) {
        console.error('Navigation error in AuthGuard:', navError);
        // Force redirect as fallback
        window.location.href = redirectPath;
      }
    }
  }, [
    sessionToken,
    userProfile, 
    agent,
    requireSession,
    requireProfile,
    requireAgent,
    redirectPath,
    alertTitle,
    alertMessage,
    navigate,
    triggerAlert
  ]);

  // Return auth state for components that need it
  return {
    isAuthenticated: !!(
      (!requireSession || sessionToken) &&
      (!requireProfile || userProfile) &&
      (!requireAgent || agent)
    ),
    sessionToken,
    userProfile,
    agent
  };
};

/**
 * Hook specifically for pages that need full authentication
 */
export const useFullAuth = () => {
  return useAuthGuard({
    requireSession: true,
    requireProfile: true,
    requireAgent: true,
    alertMessage: "Please complete the login process to continue."
  });
};

/**
 * Hook for pages that only need session (like initial login screens)
 */
export const useSessionOnly = () => {
  return useAuthGuard({
    requireSession: true,
    requireProfile: false,
    requireAgent: false,
    alertMessage: "Session expired. Please login again."
  });
};